import React, { useEffect, useState } from "react";
import { fetchReadOnlyApprovalStatus, ReadOnlyApprovalStatus } from "../../services/approvalReadOnlyViewService";
import { probeFlowForRequest, deriveRescuedDisplay, RescuedDisplayData } from "../../services/contractFlowRescueReader";

interface Props {
  module: string;
  docId: string;
  docCode?: string;
  initialStatus: string;
  approvalSummary?: any; // Cached summary from parent
}

export const ApprovalStatusBadge: React.FC<Props> = ({ module, docId, docCode, initialStatus, approvalSummary }) => {
  const [data, setData] = useState<ReadOnlyApprovalStatus | null>(null);
  const [rescueData, setRescueData] = useState<RescuedDisplayData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
        setLoading(true);
        try {
            // 1. Try specialized probing for Contract Reviews (requested fallback)
            if (module === 'contractDraft' || module === 'CONTRACT_REVIEW') {
                const rescued = await probeFlowForRequest(docId, module);
                if (active && rescued) {
                    setRescueData(deriveRescuedDisplay(rescued.id, rescued.data));
                    setLoading(false);
                    return;
                }
            }

            // 2. Fallback to standard read-only fetcher
            if (!approvalSummary?.overallStatus) {
                const res = await fetchReadOnlyApprovalStatus(module, docId, docCode);
                if (active) setData(res);
            }
        } catch(e) {
            console.error("[Badge] Load Error", e);
        } finally {
            if (active) setLoading(false);
        }
    };

    load();
    return () => { active = false; };
  }, [module, docId, docCode, approvalSummary]);

  // Determine priority of display
  let displayStatus = initialStatus;
  let displayLabel = "";

  if (rescueData) {
      displayStatus = rescueData.displayStatus;
      displayLabel = rescueData.currentStepLabel;
  } else if (approvalSummary?.overallStatus) {
      displayStatus = approvalSummary.overallStatus;
      displayLabel = approvalSummary.currentStageLabel || "";
  } else if (data) {
      displayStatus = data.status;
      displayLabel = data.currentStageLabel;
  }

  const getStatusColor = (s: string) => {
      if (["FULLY_APPROVED", "APPROVED", "PUBLISHED", "COMPLETED"].includes(s)) return "var(--ok)";
      if (["REJECTED", "RETURNED", "LEGAL_RETURNED", "CANCELLED"].includes(s)) return "var(--danger)";
      if (s === "WAITING_APPROVAL" || s === "PENDING_MANAGER") return "#8b5cf6";
      if (s === "SUBMITTED" || s === "IN_REVIEW" || s === "PENDING_LEGAL") return "#fbbf24";
      return "#666";
  };

  const statusMap: any = {
      'IN_REVIEW': '審查中',
      'SUBMITTED': '已送件',
      'WAITING_APPROVAL': '主管核決',
      'PENDING_MANAGER': '主管核決',
      'APPROVED': '已核准',
      'FULLY_APPROVED': '流程完成',
      'REJECTED': '已駁回',
      'RETURNED': '已退回',
      'LEGAL_RETURNED': '法務退回',
      'DRAFT': '草稿',
      'CANCELLED': '已作廢',
      'COMPLETED': '已完成'
  };

  const color = getStatusColor(displayStatus);
  const text = statusMap[displayStatus] || displayStatus;

  if (loading) {
      return <span style={{fontSize:12, color:'#666', opacity: 0.6}}>讀取中...</span>;
  }

  return (
    <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
        <span className="badge" style={{ color, borderColor: color }}>
            {text}
        </span>
        {displayLabel && (
            <span style={{fontSize:11, color:'#aaa', marginTop:2}}>
                {displayLabel}
            </span>
        )}
    </div>
  );
};